import {
  CheckoutCompletedItem,
  CheckoutCompletedQuote,
  CompletedQuoteDetails,
} from '@checkout/util-models';
import {
  BasketItemQuoteDetails,
  BasketItemType,
  BasketSelectors,
  calculateQuoteNumberOfPayments,
  QuoteDetails,
} from '@domgen/dgx-fe-basket';
import { CustomerDetails } from '@domgen/dgx-fe-common';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  checkoutCompletedItemsOfType,
  checkoutCompletedQuoteToCompletedQuoteDetails,
} from '../utils/checkout/checkout.utils';
import { CHECKOUT_FEATURE_KEY, CheckoutState } from './checkout.reducer';

export const selectCheckoutState =
  createFeatureSelector<CheckoutState>(CHECKOUT_FEATURE_KEY);

export const selectCompletedItems = createSelector(
  selectCheckoutState,
  (state: CheckoutState): CheckoutCompletedItem[] => state.completedItems
);

export const selectHasCompletedItems = createSelector(
  selectCompletedItems,
  (completedItems: CheckoutCompletedItem[]): boolean =>
    completedItems.length >= 1
);

export const selectCompletedItemsQuoteIds = createSelector(
  selectCompletedItems,
  (completedItems: CheckoutCompletedItem[]): string[] =>
    completedItems.reduce(
      (quoteIds: string[], completedItem: CheckoutCompletedItem) => [
        ...quoteIds,
        ...completedItem.item.data.quotes.map(
          (quote: BasketItemQuoteDetails) => quote.quoteId
        ),
      ],
      []
    )
);

export const selectHasCompletedAllSelectedQuotes = createSelector(
  selectCompletedItemsQuoteIds,
  BasketSelectors.selectSelectedQuoteIds,
  (completedItemsQuoteIds: string[], selectedQuoteIds: string[]): boolean =>
    selectedQuoteIds.every((quoteId: string) =>
      completedItemsQuoteIds.includes(quoteId)
    )
);

export const selectCompletedQuotes = createSelector(
  selectCompletedItems,
  (completedItems: CheckoutCompletedItem[]): CompletedQuoteDetails[] => {
    return checkoutCompletedItemsOfType(
      completedItems,
      BasketItemType.Quote
    ).map((completedQuote: CheckoutCompletedQuote) =>
      checkoutCompletedQuoteToCompletedQuoteDetails(completedQuote)
    );
  }
);

export const selectFirstCompletedQuote = createSelector(
  selectCompletedQuotes,
  (
    completedQuotes: CompletedQuoteDetails[]
  ): CompletedQuoteDetails | undefined => completedQuotes[0]
);

export const selectHasCompletedQuotes = createSelector(
  selectCompletedQuotes,
  (completedQuotes: CompletedQuoteDetails[]): boolean =>
    completedQuotes.length >= 1
);

export const selectCustomerDetails = createSelector(
  selectCheckoutState,
  (state: CheckoutState): CustomerDetails | undefined => state.customerDetails
);

export const selectPurchasing = createSelector(
  selectCheckoutState,
  (state: CheckoutState): boolean => state.purchasing
);

export const selectHasMonthsFreePromotionalOffer = createSelector(
  BasketSelectors.selectBasketItemsQuotes,
  (quote: QuoteDetails[]): boolean => {
    const numPayments = calculateQuoteNumberOfPayments(quote[0]);
    return Boolean(numPayments && numPayments < 11);
  }
);
